import { supabase } from "./supabase";
import type {
  Customer,
  Shipment,
  PackageItem,
  ShipmentPackage,
  TrackingEvent,
  PricingTier,
  Admin,
  Settings,
  ShipmentCategory,
  Discount,
  PromoCode,
  DiscountCustomerUsage,
  PromoCodeUsage,
  ShipmentDiscount,
  InsertShipment,
  InsertCustomer,
} from "./supabase";
import { authService } from "./auth";
import { PostgrestError } from "@supabase/supabase-js";

// Discount operations
export const discountService = {
  async getAll(
    limit = 50,
    offset = 0
  ): Promise<{ items: Discount[]; total: number }> {
    const { data, error, count } = await supabase
      .from("discounts")
      .select("*", { count: "exact" })
      .order("created_at", { ascending: false })
      .range(offset, offset + limit - 1)
      .eq("is_active", true);

    if (error) throw error;
    return { items: data || [], total: count || 0 };
  },

  async getAllActive(
    limit = 50,
    offset = 0
  ): Promise<{ items: Discount[]; total: number }> {
    const today = new Date().toISOString();

    const {
      data,
      error,
      count,
    }: {
      data: Discount[] | null;
      error: PostgrestError | null;
      count: number | null;
    } = await supabase
      .from("discounts")
      .select("*", { count: "exact" })
      .order("created_at", { ascending: false })
      .range(offset, offset + limit - 1)
      .eq("is_active", true)
      .or(
        [
          `and(start_date.lte.${today},end_date.gte.${today})`, // bounded range covering today
          `and(start_date.is.null,end_date.is.null)`, // no dates set
          `and(start_date.is.null,end_date.gte.${today})`, // only end_date bound
          `and(start_date.lte.${today},end_date.is.null)`, // only start_date bound
        ].join(",")
      );

    if (error) throw error;

    const availableDiscounts = data?.filter((discount) => {
      // If the discount has no total usage cap
      if (!discount.total_usage_limit) return true;

      // If the usage cap hasn't been reached yet
      return discount.current_total_usage! < discount.total_usage_limit;
    });

    return { items: availableDiscounts || [], total: count || 0 };
  },

  async getById(id: string): Promise<Discount> {
    const { data, error } = await supabase
      .from("discounts")
      .select(
        `
        *,
        customer_usages:discount_customer_usage(
          *,
          customer:customers(*)
        ),
        shipment_discounts:shipment_discounts(
          *,
          shipment:shipments(*)
        )
      `
      )
      .eq("id", id)
      .single();
    if (error) throw error;
    return data;
  },

  async getCustomerUsages(
    customer_id: string
  ): Promise<DiscountCustomerUsage[]> {
    const { data, error } = await supabase
      .from("discount_customer_usage")
      .select()
      .eq("customer_id", customer_id)
      .or("usage_limit.is.null,usage_limit.neq.-1");

    if (error) throw error;

    return data;
  },

  async create(
    discount: Omit<any, "id" | "created_at" | "updated_at">,
    assignedCustomerId?: string
  ) {
    const { data: createdDiscount, error } = await supabase
      .from("discounts")
      .insert([discount])
      .select()
      .single();

    if (error) throw error;

    if (assignedCustomerId && createdDiscount?.id) {
      const { error: usageError } = await supabase
        .from("discount_customer_usage")
        .insert([
          {
            code_id: createdDiscount.id,
            customer_id: assignedCustomerId,
            usage_limit: discount.usage_limit_per_customer || null,
          },
        ]);
      if (usageError) throw usageError;
    }

    return createdDiscount;
  },

  async createUsage(
    discountId: string,
    newUsage: Partial<DiscountCustomerUsage>
  ) {
    const { error: usageError, data } = await supabase
      .from("discount_customer_usage")
      .insert([
        {
          code_id: discountId,
          customer_id: newUsage.customer_id,
          usage_limit: newUsage.usage_limit,
        },
      ])
      .select(
        `
        *,
          customer:customers(*)
      `
      )
      .single();

    if (usageError) throw usageError;

    return data;
  },

  async update(id: string, updates: Partial<Discount>) {
    const { data, error } = await supabase
      .from("discounts")
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq("id", id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async updateUsage(id: string, updates: Partial<DiscountCustomerUsage>) {
    const { data, error } = await supabase
      .from("discount_customer_usage")
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;

    return data;
  },

  async deactivate(id: string) {
    const { data, error } = await supabase
      .from("discounts")
      .update({ is_active: false, updated_at: new Date().toISOString() })
      .eq("id", id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },
};

// Promo Code operations
export const promoService = {
  async getAll(
    limit = 50,
    offset = 0
  ): Promise<{ items: PromoCode[]; total: number }> {
    const { data, error, count } = await supabase
      .from("promo_codes")
      .select("*", { count: "exact" })
      .order("created_at", { ascending: false })
      .range(offset, offset + limit - 1)
      .eq("is_active", true);

    if (error) throw error;
    return { items: data || [], total: count || 0 };
  },

  async getAllActive(
    limit = 50,
    offset = 0
  ): Promise<{ items: PromoCode[]; total: number }> {
    const today = new Date().toISOString();

    const {
      data,
      error,
      count,
    }: {
      data: PromoCode[] | null;
      error: PostgrestError | null;
      count: number | null;
    } = await supabase
      .from("promo_codes")
      .select("*", { count: "exact" })
      .order("created_at", { ascending: false })
      .range(offset, offset + limit - 1)
      .eq("is_active", true)
      .lte("start_date", today)
      .gte("end_date", today);

    if (error) throw error;

    const availablePromos = data?.filter((promo) => {
      // If the promo has no total usage cap
      if (!promo.total_usage_limit) return true;

      // If the usage cap hasn't been reached yet
      return promo.current_total_usage! < promo.total_usage_limit;
    });

    return { items: availablePromos || [], total: count || 0 };
  },

  async getById(id: string) {
    const { data, error } = await supabase
      .from("promo_codes")
      .select(
        `
        *,
        usages:promo_code_usage(
          *,
          customer:customers(*)
        ),
        shipment_discounts:shipment_discounts(
          *,
          shipment:shipments(*)
        )
      `
      )
      .eq("id", id)
      .single();

    if (error) throw error;

    return data;
  },

  async getCustomerUsages(customer_id: string): Promise<PromoCodeUsage[]> {
    const { data, error } = await supabase
      .from("promo_code_usage")
      .select()
      .eq("customer_id", customer_id);

    if (error) throw error;

    return data;
  },

  async create(promo: Omit<any, "id" | "created_at" | "updated_at">) {
    const { data, error } = await supabase
      .from("promo_codes")
      .insert([promo])
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async update(id: string, updates: Partial<PromoCode>) {
    const { data, error } = await supabase
      .from("promo_codes")
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq("id", id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async deactivate(id: string) {
    const { data, error } = await supabase
      .from("promo_codes")
      .update({ is_active: false, updated_at: new Date().toISOString() })
      .eq("id", id)
      .select()
      .single();
    if (error) throw error;
    return data;
  },
};

// Customer operations
export const customerService = {
  async searchCustomersAndShipments(query: string, limit = 20) {
    try {
      // Search customers by name, email, and phone
      const { data: customers, error: customerError } = await supabase
        .from("customers")
        .select("id, first_name, last_name, email, phone, created_at")
        .or(
          `first_name.ilike.%${query}%,last_name.ilike.%${query}%,email.ilike.%${query}%,phone.ilike.%${query}%`
        )
        .limit(limit);

      if (customerError) {
        console.error("Customer search error:", customerError);
        throw customerError;
      }

      // Search shipments by tracking number only
      const { data: shipments, error: shipmentError } = await supabase
        .from("shipments")
        .select(
          `
          id, tracking_number, status, created_at, shipping_method, total_cost, dimensions, weight,
          customer:customers(first_name, last_name, email)
        `
        )
        .ilike("tracking_number", `%${query}%`)
        .limit(limit);

      if (shipmentError) {
        console.error("Shipment search error:", shipmentError);
        throw shipmentError;
      }

      // Search for shipments by customer name using a different approach
      // First get customer IDs that match the search
      const matchingCustomerIds = customers?.map((c) => c.id) || [];

      let shipmentsByCustomer: any[] = [];
      if (matchingCustomerIds.length > 0) {
        const { data: customerShipments, error: customerShipmentsError } =
          await supabase
            .from("shipments")
            .select(
              `
            id, tracking_number, status, created_at, shipping_method, total_cost, dimensions, weight,
            customer:customers(first_name, last_name, email)
          `
            )
            .in("customer_id", matchingCustomerIds)
            .limit(limit);

        if (customerShipmentsError) {
          console.error(
            "Customer shipments search error:",
            customerShipmentsError
          );
        } else {
          shipmentsByCustomer = customerShipments || [];
        }
      }

      // Combine and deduplicate shipments
      const allShipments = [...(shipments || []), ...shipmentsByCustomer];
      const uniqueShipments = allShipments.filter(
        (shipment, index, self) =>
          index === self.findIndex((s) => s.id === shipment.id)
      );

      return {
        customers: customers || [],
        shipments: uniqueShipments,
      };
    } catch (error) {
      console.error("Search error:", error);
      // Return empty results instead of throwing to prevent UI crashes
      return {
        customers: [],
        shipments: [],
      };
    }
  },

  async getAll(limit = 50, offset = 0) {
    const { data, error, count } = await supabase
      .from("customers")
      .select("*", { count: "exact" })
      .order("created_at", { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) throw error;
    return { customers: data || [], total: count || 0 };
  },

  async getById(id: string) {
    const { data, error } = await supabase
      .from("customers")
      .select("*")
      .eq("id", id)
      .single();

    if (error) throw error;
    return data;
  },

  async search(query: string, limit = 20) {
    const { data, error } = await supabase
      .from("customers")
      .select("*")
      .or(
        `first_name.ilike.%${query}%,last_name.ilike.%${query}%,email.ilike.%${query}%`
      )
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  },

  async create(
    customer: Omit<
      InsertCustomer,
      "account_status" | "created_at" | "customer_tier" | "date_of_birth"
    >,
    createAuth = true
  ) {
    let customerData;

    if (createAuth) {
      // TBD: Call the create-user edge function here
    } else {
      console.log("Creating customer without auth");
      // Create customer without auth
      const { data, error } = await supabase
        .from("customers")
        .insert([{ ...customer }])
        .select()
        .single();

      customerData = data;

      if (error) throw error;
    }

    return customerData;
  },

  async createWithNotification(
    customer: Omit<
      Customer,
      "id" | "created_at" | "updated_at" | "customer_tier" | "account_status"
    >,
    createAuth = true,
    notificationMethod: "email" | "sms" | "both" = "email"
  ) {
    if (createAuth) {
      // Generate a random password
      const authPassword = generatePassword();

      try {
        // Create auth user first
        const authUser = await authService.signUp(
          customer.email,
          authPassword,
          customer,
          "customer"
        );

        // Send welcome notification via chosen method
        if (notificationMethod === "email") {
          await sendWelcomeEmail(
            customer.email,
            customer.first_name,
            authPassword
          );
        } else {
          await sendWelcomeSMS(
            customer.phone!,
            customer.first_name,
            authPassword
          );
        }

        return authUser;
      } catch (error) {
        console.error("Failed to create auth user:", error);
        throw new Error("Failed to create customer account");
      }
    }

    // Create customer without auth
    const { data, error } = await supabase
      .from("customers")
      .insert([{ ...customer }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async update(id: string, updates: Partial<Customer>) {
    const { data, error } = await supabase
      .from("customers")
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async activateAccount(
    customerId: string,
    customerEmail: string,
    customerName: string
  ) {
    // Generate new password
    const newPassword = generatePassword();

    try {
      // Update auth user password
      const { error: authError } = await supabase.auth.admin.updateUserById(
        customerId,
        {
          password: newPassword,
        }
      );

      if (authError) throw authError;

      // Send email with new credentials
      await sendWelcomeEmail(customerEmail, customerName, newPassword);

      return { success: true };
    } catch (error) {
      console.error("Failed to activate account:", error);
      throw new Error("Failed to activate customer account");
    }
  },

  async deactivateAccount(customerId: string) {
    try {
      // Disable auth user
      const { error: authError } = await supabase.auth.admin.updateUserById(
        customerId,
        {
          user_metadata: { disabled: true },
        }
      );

      if (authError) throw authError;

      return { success: true };
    } catch (error) {
      console.error("Failed to deactivate account:", error);
      throw new Error("Failed to deactivate customer account");
    }
  },

  async getTopCustomers(limit = 10) {
    const { data, error } = await supabase
      .from("customers")
      .select(
        `
        *,
        shipments:shipments(count)
      `
      )
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  },

  async getTopCustomersWithStats(limit = 5) {
    const { data, error } = await supabase
      .from("customers")
      .select(
        `
        id, first_name, last_name, email,
        shipments:shipments(total_cost)
      `
      )
      .limit(limit);

    if (error) throw error;

    // Calculate stats for each customer
    const customersWithStats = (data || [])
      .map((customer) => {
        const totalSpent =
          customer.shipments?.reduce(
            (sum, shipment) => sum + Number(shipment.total_cost),
            0
          ) || 0;
        const totalShipments = customer.shipments?.length || 0;

        return {
          ...customer,
          totalSpent,
          totalShipments,
        };
      })
      .filter((customer) => customer.totalSpent > 0) // Only show customers with >$0 spent
      .sort((a, b) => b.totalSpent - a.totalSpent);

    return customersWithStats;
  },
};

export const adminService = {
  async getAll(limit = 50, offset = 0) {
    const { data, error, count } = await supabase
      .from("admins")
      .select("*", { count: "exact" })
      .order("created_at", { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) throw error;
    return { admins: data || [], total: count || 0 };
  },

  async getById(id: string) {
    const { data, error } = await supabase
      .from("admins")
      .select("*")
      .eq("id", id)
      .single();

    if (error) throw error;
    return data;
  },

  async search(query: string, limit = 20) {
    const { data, error } = await supabase
      .from("admins")
      .select("*")
      .or(`name.ilike.%${query}%,email.ilike.%${query}%`)
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  },

  async create(
    admin: Omit<Admin, "id" | "created_at" | "updated_at">,
    createAuth = true
  ) {
    let adminData;

    if (createAuth) {
      // TBD: Call the create-user edge function here
    } else {
      // Create admin without auth
      const { data, error } = await supabase
        .from("admins")
        .insert([admin])
        .select()
        .single();

      adminData = data;

      if (error) throw error;
    }

    return adminData;
  },

  async update(id: string, updates: Partial<Admin>) {
    const { data, error } = await supabase
      .from("admins")
      .update({ ...updates })
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },
};

// Shipment operations
export const shipmentService = {
  async getAll(limit = 50, offset = 0) {
    const { data, error, count } = await supabase
      .from("shipments")
      .select(
        `
        *,
        customer:customers(*),
        packages:shipment_packages(
          *,
          items:package_items(*)
        ),
        tracking_events:tracking_events(*)
      `,
        { count: "exact" }
      )
      .order("created_at", { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) throw error;
    return { shipments: data || [], total: count || 0 };
  },

  async getAllCategories() {
    const { data, error } = await supabase
      .from("categories")
      .select("*")
      .order("name", { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async getRecent(limit = 10) {
    const { data, error } = await supabase
      .from("shipments")
      .select(
        `
        *,
        customer:customers(*),
        packages:shipment_packages(
          *,
          items:package_items(*)
        ),
        tracking_events:tracking_events(*)
      `
      )
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  },

  async getByCustomerId(customerId: string, limit = 10) {
    const { data, error } = await supabase
      .from("shipments")
      .select(
        `
        *,
        customer:customers(*),
        packages:shipment_packages(
          *,
          items:package_items(*)
        ),
        tracking_events:tracking_events(*)
      `
      )
      .eq("customer_id", customerId)
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  },

  async getByTrackingNumber(trackingNumber: string): Promise<Shipment> {
    const { data, error } = await supabase
      .from("shipments")
      .select(
        `
        *,
        customer:customers(*),
        packages:shipment_packages(
          *,
          items:package_items(
            *,
            category:categories(name)
          )
        ),
        codes:shipment_discounts(
          *,
          discount:discounts(*),
          promo:promo_codes(*)
        )
      `
      )
      .eq("tracking_number", trackingNumber)
      .single();

    if (error) throw error;
    return data;
  },

  async search(query: string, limit = 20) {
    const { data, error } = await supabase
      .from("shipments")
      .select(
        `
        *,
        customer:customers(*),
        packages:shipment_packages(
          *,
          items:package_items(*)
        ),
        tracking_events:tracking_events(*)
      `
      )
      .ilike("tracking_number", `%${query}%`)
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  },

  async create(
    shipment: InsertShipment,
    packages: {
      package: Omit<ShipmentPackage, "id" | "shipment_id" | "created_at">;
      items: Omit<PackageItem, "id" | "shipment_package_id" | "created_at">[];
    }[] = []
  ) {
    const { count } = await supabase
      .from("shipments")
      .select("id", { count: "exact" });

    const shipmentIndex = (count || 0) + 1;
    const tracking_number = `108SHI${new Date().getFullYear()}${shipmentIndex
      .toString()
      .padStart(3, "0")}`;

    const { data, error } = await supabase
      .from("shipments")
      .insert([
        {
          ...shipment,
          tracking_number,
        },
      ])
      .select(
        `
        *,
        customer:customers(*)
      `
      )
      .single();

    if (error) throw error;

    if (packages.length > 0) {
      const allItems: Omit<
        PackageItem,
        "id" | "shipment_package_id" | "created_at"
      >[] = [];

      for (const pkg of packages) {
        const shipmentPackage = await shipmentPackageService.create({
          shipment_id: data.id,
          ...pkg.package,
        });

        if (pkg.items.length) {
          await packageItemService.createMultiple(
            shipmentPackage.id,
            pkg.items
          );
          allItems.push(...pkg.items);
        }
      }

      if (allItems.length) {
        await shipmentService.createShipmentCategories(data.id, allItems);
      }
    }

    // Create initial tracking event
    await trackingEventService.create({
      shipment_id: data.id,
      status: data.status,
      location: "Admin dashboard",
      description: getStatusDescription(data.status),
      created_by: shipment.admin_id!,
      is_public: true,
      event_timestamp: new Date().toISOString(),
    });

    return data;
  },

  async createShipmentDiscounts(
    shipmentDiscounts: Partial<ShipmentDiscount>[]
  ): Promise<void> {
    const { error } = await supabase
      .from("shipment_discounts")
      .insert(shipmentDiscounts);

    if (error) throw error;
  },

  async generateTrackingNumber(): Promise<string> {
    const { count } = await supabase
      .from("shipments")
      .select("id", { count: "exact" });

    const shipmentIndex = (count || 0) + 1;
    return `108SH${new Date().getFullYear()}${shipmentIndex
      .toString()
      .padStart(3, "0")}`;
  },

  async update(id: string, updates: Partial<Shipment>) {
    const { data, error } = await supabase
      .from("shipments")
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq("id", id)
      .select(
        `
        *,
        customer:customers(*),
        packages:shipment_packages(
          *,
          items:package_items(*)
        ),
        tracking_events:tracking_events(*)
      `
      )
      .single();

    if (error) throw error;
    return data;
  },

  async updateStatus(
    id: string,
    status: string,
    location?: string,
    description?: string,
    adminId?: string
  ) {
    // Update shipment status
    const { data: shipment, error: shipmentError } = await supabase
      .from("shipments")
      .update({ status, updated_at: new Date().toISOString() })
      .eq("id", id)
      .select(
        `
        *,
        customer:customers(*),
        packages:shipment_packages(
          *,
          items:package_items(*)
        ),
        tracking_events:tracking_events(*)
      `
      )
      .single();

    if (shipmentError) throw shipmentError;

    // Create tracking event
    await trackingEventService.create({
      shipment_id: id,
      status,
      location,
      description: description || getStatusDescription(status),
      created_by: adminId,
      is_public: true,
      event_timestamp: new Date().toISOString(),
    });

    return shipment;
  },

  async getStats() {
    const { data: totalShipments, error: totalError } = await supabase
      .from("shipments")
      .select("id", { count: "exact" });

    const { data: activeShipments, error: activeError } = await supabase
      .from("shipments")
      .select("id", { count: "exact" })
      .in("status", [
        "created",
        "estimated",
        "confirmed",
        "picking_up_from_sender",
        "received_at_warehouse",
        "processing_at_warehouse",
        "in_transit_to_country",
        "arrived_at_warehouse",
        "ready_for_pickup",
        "in_transit_to_recipient",
      ]);

    const { data: revenueData, error: revenueError } = await supabase
      .from("shipments")
      .select("total_cost");

    if (totalError || activeError || revenueError) {
      throw totalError || activeError || revenueError;
    }

    const totalRevenue =
      revenueData?.reduce(
        (sum, shipment) => sum + Number(shipment.total_cost),
        0
      ) || 0;

    return {
      totalShipments: totalShipments?.length || 0,
      activeShipments: activeShipments?.length || 0,
      totalRevenue,
    };
  },

  async createShipmentCategories(
    shipmentId: string,
    items: Omit<PackageItem, "id" | "shipment_package_id" | "created_at">[]
  ): Promise<ShipmentCategory[]> {
    const itemCategoryIds = items
      .map((item) => item.category_id)
      .filter(Boolean) as string[];
    const uniqueCategoryIds = Array.from(new Set(itemCategoryIds));

    const categoriesWithShipment = uniqueCategoryIds.map((category_id) => ({
      shipment_id: shipmentId,
      category_id,
    }));

    const { data, error } = await supabase
      .from("shipment_categories")
      .insert(categoriesWithShipment)
      .select();

    if (error) throw error;
    return data;
  },
};

// Shipment Packages operations
export const shipmentPackageService = {
  async create(pkg: Omit<ShipmentPackage, "id" | "created_at">) {
    const { data, error } = await supabase
      .from("shipment_packages")
      .insert([pkg])
      .select()
      .single();

    if (error) throw error;
    return data;
  },
};

// Package Items operations
export const packageItemService = {
  async create(item: Omit<PackageItem, "id" | "created_at">) {
    const { data, error } = await supabase
      .from("package_items")
      .insert([item])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async createMultiple(
    packageId: string,
    items: Omit<PackageItem, "id" | "shipment_package_id" | "created_at">[]
  ) {
    const itemsWithPackageId = items.map((item) => ({
      ...item,
      shipment_package_id: packageId,
    }));

    const { data, error } = await supabase
      .from("package_items")
      .insert(itemsWithPackageId)
      .select();

    if (error) throw error;
    return data;
  },

  async getByShipmentId(shipmentId: string) {
    const { data: packages, error } = await supabase
      .from("shipment_packages")
      .select("id")
      .eq("shipment_id", shipmentId);

    if (error) throw error;

    const packageIds = packages?.map((pkg) => pkg.id) || [];
    if (!packageIds.length) return [];

    const { data: items, error: itemsError } = await supabase
      .from("package_items")
      .select("*")
      .in("shipment_package_id", packageIds)
      .order("created_at", { ascending: true });

    if (itemsError) throw itemsError;
    return items || [];
  },

  async update(id: string, updates: Partial<PackageItem>) {
    const { data, error } = await supabase
      .from("package_items")
      .update(updates)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id: string) {
    const { error } = await supabase
      .from("package_items")
      .delete()
      .eq("id", id);

    if (error) throw error;
  },
};

// Tracking Events operations
export const trackingEventService = {
  async create(event: Omit<TrackingEvent, "id" | "created_at">) {
    const { data, error } = await supabase
      .from("tracking_events")
      .insert([
        {
          ...event,
          event_timestamp: event.event_timestamp || new Date().toISOString(),
        },
      ])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async createMultiple(events: Omit<TrackingEvent, "id" | "created_at">[]) {
    const { data, error } = await supabase
      .from("tracking_events")
      .insert(
        events.map((event) => ({
          ...event,
          event_timestamp: event.event_timestamp || new Date().toISOString(),
        }))
      )
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getByShipmentId(shipmentId: string, publicOnly = false) {
    let query = supabase
      .from("tracking_events")
      .select("*")
      .eq("shipment_id", shipmentId);

    if (publicOnly) {
      query = query.eq("is_public", true);
    }

    const { data, error } = await query.order("event_timestamp", {
      ascending: true,
    });

    if (error) throw error;
    return data || [];
  },

  async update(id: string, updates: Partial<TrackingEvent>) {
    const { data, error } = await supabase
      .from("tracking_events")
      .update(updates)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async delete(id: string) {
    const { error } = await supabase
      .from("tracking_events")
      .delete()
      .eq("id", id);

    if (error) throw error;
  },
};

// Pricing Tiers operations
export const pricingTierService = {
  async getAll(): Promise<PricingTier[]> {
    const { data, error } = await supabase
      .from("pricing_tiers")
      .select("*")
      .eq("is_active", true)
      .order("tier_order", { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async update(id: string, updates: Partial<PricingTier>) {
    const { data, error } = await supabase
      .from("pricing_tiers")
      .update(updates)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteMultiple(tierIds: PricingTier["id"][]) {
    const { error } = await supabase
      .from("pricing_tiers")
      .delete()
      .in("id", tierIds);

    if (error) throw error;
  },

  async updateMultiple(tiers: PricingTier[]) {
    // Update the existing tiers
    const existingTiers = tiers.filter((tier) => !tier.id.startsWith("new"));

    const { error: updateError } = await supabase
      .from("pricing_tiers")
      .upsert(existingTiers)
      .select();

    if (updateError) throw updateError;

    // Insert new tiers
    const newTiers = tiers
      .filter((tier) => tier.id.startsWith("new"))
      .map(({ id, created_at, ...tier }) => tier);

    const { error: insertError } = await supabase
      .from("pricing_tiers")
      .insert(newTiers);

    if (insertError) throw insertError;

    const { data } = await supabase
      .from("pricing_tiers")
      .select("*")
      .order("tier_order", { ascending: true });

    return data || [];
  },

  async getAirRateForWeight(weight: number): Promise<number> {
    const { data, error } = await supabase
      .from("pricing_tiers")
      .select("rate")
      .lte("min_weight", weight)
      .or(`max_weight.gte.${weight},max_weight.is.null`)
      .eq("is_active", true)
      .order("min_weight", { ascending: false })
      .limit(1)
      .single();

    if (error || !data) return 4.75; // Default rate
    return data.rate;
  },
};

// Settings operations
export const settingsService = {
  async getAll() {
    const { data, error } = await supabase
      .from("settings")
      .select("*")
      .order("key");

    if (error) throw error;
    return data || [];
  },

  async getByKey(key: string) {
    const { data, error } = await supabase
      .from("settings")
      .select("*")
      .eq("key", key)
      .single();

    if (error) throw error;
    return data;
  },

  async update(key: string, value: string) {
    const { data, error } = await supabase
      .from("settings")
      .update({ value, updated_at: new Date().toISOString() })
      .eq("key", key)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getEstimationSettings(): Promise<Record<string, number>> {
    const {
      data,
      error,
    }: {
      data: Pick<Settings, "key" | "value">[] | null;
      error: PostgrestError | null;
    } = await supabase
      .from("settings")
      .select("key, value")
      .in("key", [
        "express_shipping_max_transit_days",
        "express_shipping_min_transit_days",
        "sea_shipping_max_transit_days",
        "sea_shipping_min_transit_days",
        "air_shipping_max_transit_days",
        "air_shipping_min_transit_days",
      ]);

    if (error) throw error;

    const settings: Record<string, number> =
      data?.reduce((acc, setting) => {
        acc[setting.key] = parseFloat(setting.value);
        return acc;
      }, {} as Record<string, number>) || {};

    return settings || [];
  },

  async getPricingSettings() {
    const { data, error } = await supabase
      .from("settings")
      .select("key, value")
      .in("key", [
        "sea_rate_per_cft",
        "delivery_fee_min",
        "delivery_fee_max",
        "pickup_fee_min",
        "pickup_fee_max",
        "express_shipping_fee",
      ]);

    if (error) throw error;

    const settings =
      data?.reduce((acc, setting) => {
        acc[setting.key] = parseFloat(setting.value);
        return acc;
      }, {} as Record<string, number>) || {};

    return {
      seaRate: settings.sea_rate_per_cft || 20.0,
      deliveryFeeMin: settings.delivery_fee_min || 10.0,
      deliveryFeeMax: settings.delivery_fee_max || 25.0,
      pickupFeeMin: settings.pickup_fee_min || 5.0,
      pickupFeeMax: settings.pickup_fee_max || 15.0,
      expressFee: settings.express_shipping_fee || 4.75,
    };
  },
};

// Utility functions
function generatePassword(): string {
  const chars = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  let password = "";
  for (let i = 0; i < 12; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
}

async function sendWelcomeEmail(
  email: string,
  firstName: string,
  password: string
) {
  // In a real application, you would use an email service like SendGrid, Mailgun, etc.
  // For now, we'll just log the email content
  console.log(`
    Welcome Email for ${email}:
    
    Subject: Welcome to 108 Shipping & Services
    
    Dear ${firstName},
    
    Your account has been created successfully!
    
    Login Details:
    Email: ${email}
    Password: ${password}
    
    Please log in at: ${window.location.origin}/auth/login
    
    For security, please change your password after your first login.
    
    Best regards,
    108 Shipping & Services Team
  `);

  // TODO: Implement actual email sending
  // Example with a hypothetical email service:
  // await emailService.send({
  //   to: email,
  //   subject: 'Welcome to 108 Shipping & Services',
  //   template: 'welcome',
  //   data: { firstName, email, password, loginUrl: `${window.location.origin}/auth/login` }
  // })
}

async function sendWelcomeSMS(
  phone: string,
  firstName: string,
  password: string
) {
  // In a real application, you would use Twilio or similar SMS service
  console.log(`
    SMS to ${phone}:
    
    Hi ${firstName}! Your 108 Shipping account is ready.
    Password: ${password}
    Login at: ${window.location.origin}/auth/login
    
    Change your password after first login for security.
  `);

  // TODO: Implement actual SMS sending with Twilio
  // Example:
  // await twilioClient.messages.create({
  //   body: `Hi ${firstName}! Your 108 Shipping account is ready. Password: ${password}`,
  //   from: process.env.TWILIO_PHONE_NUMBER,
  //   to: phone
  // })
}

function getStatusDescription(status: string): string {
  const descriptions: Record<string, string> = {
    created: "Shipment has been created",
    estimated: "Shipment cost and charges have been estimated",
    confirmed: "Shipment has been confirmed",

    picking_up_from_sender: "Pickup scheduled from customer address",
    received_at_warehouse: "Package received at warehouse",
    processing_at_warehouse:
      "Package being processed and prepared for shipment",
    in_transit_to_country: "Package in transit to destination country",
    arrived_at_warehouse: "Package arrived at destination facility",

    in_transit_to_recipient: "Package out for delivery",
    delivered_to_recipient: "Package delivered to recipient",

    ready_for_pickup: "Package ready for pickup",
    picked_up_by_recipient: "Package picked up by recipient",

    cancelled: "Shipment has been cancelled",
    refunded: "Shipment has been refunded",
  };

  return descriptions[status] || `Status updated to ${status}`;
}

// Utility function to calculate shipping cost
export function calculateAirShippingCost(
  weight: number,
  airRate: number,
  includeDelivery: boolean = false,
  deliveryFee: number = 0
): number {
  const basePrice = weight * airRate;

  return Math.round(basePrice * 100) / 100;
}

export function calculateSeaShippingCost(
  length: number,
  width: number,
  height: number,
  seaRate: number,
  includeDelivery: boolean = false,
  deliveryFee: number = 0
): number {
  // Convert inches to feet and calculate cubic feet
  const cubicFeet = (length * width * height) / 1728; // 1728 cubic inches = 1 cubic foot
  let basePrice = cubicFeet * seaRate;

  return Math.round(basePrice * 100) / 100;
}
